<template>
  <div>
    <b-row class="mb-1">
      <b-col cols="12" md="6" lg="4" class="mb-2">
        <div class="d-flex align-items-center">
          <date-picker
            placeholder="Từ ngày"
            class="form-control form-control-sm"
            :config="dpConfigs.date"
            v-model="filter.fromDate"
            :disabled="isProcessing"
          ></date-picker>
          <span class="mx-1"></span>
          <date-picker
            placeholder="Đến ngày"
            class="form-control form-control-sm"
            :config="dpConfigs.date"
            v-model="filter.toDate"
            :disabled="isProcessing"
          ></date-picker>
        </div>
      </b-col>
      <b-col cols="12" sm="6" md="3" lg="2" class="mb-2">
        <b-form-input
          size="sm"
          v-model="filter.voucherCode"
          placeholder="Mã voucher"
          :disabled="isProcessing"
          @keyup.enter="onFilter"
        />
      </b-col>
      <b-col cols="12" sm="6" md="3" lg="2" class="mb-2">
        <b-form-select
          size="sm"
          v-model="filter.storeId"
          :options="storeOptions"
          value-field="id"
          text-field="name"
          :disabled="isProcessing"
        />
      </b-col>
      <b-col cols="12" sm="6" md="3" lg="2" class="mb-2">
        <b-form-input
          size="sm"
          v-model="filter.customerMobile"
          placeholder="SĐT khách"
          :disabled="isProcessing"
          @keyup.enter="onFilter"
        />
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="12">
        <b-button
          style="font-weight: 600; width: 70px"
          variant="primary"
          size="sm"
          :disabled="isProcessing"
          @click="onFilter"
          >Lọc</b-button
        >
      </b-col>
    </b-row>

    <div class="mb-3">
      <span class="mr-4"
        >Tổng lượt dùng: <b>{{ summary.totalUsed }}</b></span
      >
      <span class="mr-2"
        >Tổng tiền giảm: <b>{{ formatMoney(summary.totalDiscount) }}</b></span
      >
      <span v-if="summary.legacyWithoutAmount" class="text-muted small"
        >(Chưa gồm {{ summary.legacyWithoutAmount }} lượt không có tiền giảm)</span
      >
    </div>

    <b-table
      :items="items"
      :fields="fields"
      :busy="onLoading"
      bordered
      hover
      small
      responsive
      show-empty
    >
      <template #table-busy>
        <div class="text-center py-3">
          <b-spinner small variant="primary" />
        </div>
      </template>
      <template #empty>
        <p class="text-center text-muted mb-0">{{ emptyText }}</p>
      </template>
      <template #cell(billCreatedAt)="{ value }">
        <div class="text-nowrap">{{ formatDay(value) }}</div>
        <div class="small text-muted">{{ formatHour(value) }}</div>
      </template>
      <template #cell(billNumber)="{ item }">
        <a :href="billLink(item)" target="_blank" rel="noopener">{{
          item.billNumber
        }}</a>
      </template>
      <template #cell(customer)="{ item }">
        <div class="text-break">{{ item.customerName }}</div>
        <div v-if="item.customerMobile" class="small text-muted text-nowrap">
          {{ item.customerMobile }}
        </div>
      </template>
      <template #cell(productName)="{ value }">
        <span class="text-break">{{ value }}</span>
      </template>
      <template #cell(discountMoney)="{ value }">
        <span class="text-nowrap">{{
          value === null ? '—' : formatMoney(value)
        }}</span>
      </template>
      <template #cell(isBillDeleted)="{ item }">
        <span
          v-if="item.isBillDeleted"
          class="label font-weight-bold label-lg label-light-danger label-inline"
          >Đã hủy</span
        >
        <template v-else-if="item.isRemoved">
          <span class="label font-weight-bold label-lg label-light-warning label-inline"
            >Đã gỡ</span
          >
          <div v-if="item.removedAt" class="small text-muted text-nowrap">
            {{ formatTime(item.removedAt) }}
          </div>
        </template>
      </template>
    </b-table>

    <b-pagination
      v-if="totalPage > 1"
      v-model="page"
      :total-rows="totalRow"
      :per-page="limit"
      :disabled="isProcessing"
      size="sm"
      align="right"
      @change="onChangePage"
    ></b-pagination>
  </div>
</template>

<script>
import moment from 'moment';
import ApiService from '@/core/services/api.service';
import timeUtils, { formatSpecificDate } from '@/utils/date';
import { makeToastFaile, getApiErrorMessage, convertPrice } from '@/utils/common';
import { VOUCHER_HISTORY_MESSAGES } from '@/utils/constants';
import { BILL_TYPE } from '@/utils/enum';
import fileDownload from '@/utils/file-download';
import submitLock from '@/view/mixins/submit-lock.mixins';

const FORBIDDEN = 403;

const emptyFilter = () => ({
  voucherCode: '',
  storeId: null,
  customerMobile: '',
  fromDate: '',
  toDate: '',
});

export default {
  name: 'VoucherUsageHistory',
  mixins: [submitLock],
  props: {
    voucherId: { type: [Number, String], required: true },
  },
  data() {
    return {
      dpConfigs: timeUtils.DP_CONFIG,
      filter: emptyFilter(),
      // Bộ lọc đã gửi lần gần nhất — Xuất Excel theo đúng bộ lọc đang xem
      appliedParams: {},
      storeOptions: [{ id: null, name: 'Tất cả cửa hàng' }],
      items: [],
      summary: { totalUsed: 0, totalDiscount: 0, legacyWithoutAmount: 0 },
      page: 1,
      limit: 10,
      totalRow: 0,
      totalPage: 0,
      onLoading: false,
      isFiltered: false,
      fields: [
        { key: 'voucherCode', label: 'Mã', tdClass: 'text-nowrap' },
        { key: 'billNumber', label: 'Số HĐ', tdClass: 'text-nowrap' },
        { key: 'billCreatedAt', label: 'Ngày', thStyle: { width: '100px' } },
        { key: 'storeName', label: 'Cửa hàng', thStyle: { minWidth: '140px' } },
        { key: 'customer', label: 'Khách hàng', thStyle: { minWidth: '160px' } },
        { key: 'cashierName', label: 'Thu ngân', thClass: 'text-nowrap' },
        { key: 'productName', label: 'Sản phẩm', thStyle: { minWidth: '160px' } },
        {
          key: 'quantity',
          label: 'SL',
          thStyle: { width: '50px' },
          tdClass: 'text-center',
        },
        { key: 'discountMoney', label: 'Giảm', tdClass: 'text-right' },
        {
          key: 'isBillDeleted',
          label: 'Trạng thái',
          thStyle: { width: '120px' },
          tdClass: 'text-center',
        },
      ],
    };
  },
  computed: {
    isProcessing() {
      return this.onLoading || this.isSubmitting('export');
    },
    emptyText() {
      return this.isFiltered
        ? VOUCHER_HISTORY_MESSAGES.NO_MATCH
        : VOUCHER_HISTORY_MESSAGES.NO_USAGE;
    },
  },
  watch: {
    // Màn cha đặt nút Xuất Excel trên tiêu đề khung → cần biết lúc nào phải khóa nút
    isProcessing: {
      immediate: true,
      handler(processing) {
        this.$emit('state-change', {
          processing,
          exporting: this.isSubmitting('export'),
        });
      },
    },
  },
  mounted() {
    this.fetchStores();
    this.appliedParams = this.buildParams();
    this.fetchData();
  },
  methods: {
    formatMoney(value) {
      return convertPrice(value || 0, 'vi-VN');
    },
    formatDay(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '';
    },
    formatHour(value) {
      return value ? moment(value).format('HH:mm') : '';
    },
    formatTime(value) {
      return value ? moment(value).format('HH:mm DD/MM/YYYY') : '';
    },
    billLink(item) {
      // Router chạy hash mode; hóa đơn sỉ mở màn riêng (như ListBill.vue getByType 'router-link-id')
      const path =
        item.billType === BILL_TYPE.WHOLESALE_INVOICE
          ? '#/wholesaleInvoices/update-wholesale-invoice'
          : '#/bills/update-bill';
      return `${path}?id=${item.billNumber}`;
    },
    toApiDate(value) {
      return value ? formatSpecificDate(value, 'DD/MM/YYYY', 'YYYY-MM-DD') : null;
    },
    buildParams() {
      return {
        voucherCode: this.filter.voucherCode.trim() || null,
        storeId: this.filter.storeId,
        customerMobile: this.filter.customerMobile.trim() || null,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
    },
    fetchStores() {
      ApiService.get('stores?limit=1000&page=1')
        .then((response) => {
          const stores = response.data.data.list_store || [];
          this.storeOptions = [
            { id: null, name: 'Tất cả cửa hàng' },
            ...stores.map((store) => ({ id: store.id, name: store.name })),
          ];
        })
        .catch(() => {});
    },
    onFilter() {
      if (this.isProcessing) return;
      const params = this.buildParams();
      if (params.fromDate && params.toDate && params.fromDate > params.toDate) {
        makeToastFaile(VOUCHER_HISTORY_MESSAGES.INVALID_DATE_RANGE);
        return;
      }
      this.appliedParams = params;
      this.isFiltered = Object.values(params).some((value) => value);
      this.page = 1;
      this.fetchData();
    },
    onChangePage(page) {
      this.page = page;
      this.fetchData();
    },
    showError(error) {
      const status = error && error.response && error.response.status;
      makeToastFaile(
        status === FORBIDDEN
          ? VOUCHER_HISTORY_MESSAGES.FORBIDDEN
          : getApiErrorMessage(error),
      );
    },
    async fetchData() {
      if (this.onLoading) return;
      this.onLoading = true;
      try {
        const { data } = await ApiService.query(
          `v2/vouchers/${this.voucherId}/usage-histories`,
          { params: { ...this.appliedParams, page: this.page, limit: this.limit } },
        );
        const result = data.data;
        this.items = result.dataset;
        this.totalRow = result.totalRow;
        this.totalPage = result.totalPage;
        this.summary = result.summary;
      } catch (error) {
        this.showError(error);
      } finally {
        this.onLoading = false;
      }
    },
    onExport() {
      if (this.isProcessing) return;
      return this.withSubmitLock('export', async () => {
        try {
          const response = await ApiService.query(
            `v2/vouchers/${this.voucherId}/usage-histories/export`,
            { params: this.appliedParams, responseType: 'blob' },
          );
          const fileName = `lich-su-su-dung-voucher-${this.voucherId}-${moment().format(
            'YYYYMMDD',
          )}.xlsx`;
          fileDownload.createFile(response.data, fileName);
        } catch (error) {
          // Lỗi trả về dạng blob → đọc lại JSON để lấy message (như BillExcel.vue)
          const blob = error && error.response && error.response.data;
          if (blob && typeof blob.text === 'function') {
            try {
              const body = JSON.parse(await blob.text());
              makeToastFaile(
                error.response.status === FORBIDDEN
                  ? VOUCHER_HISTORY_MESSAGES.FORBIDDEN
                  : body.message || getApiErrorMessage(error),
              );
              return;
            } catch (parseError) {
              // rơi xuống thông báo chung
            }
          }
          this.showError(error);
        }
      });
    },
  },
};
</script>
